import React from 'react';
import { FormField, HtmlLabelProps, SemanticShorthandItem } from 'semantic-ui-react';
import styled from '@emotion/styled';

type ReadonlyFormValueProps<TValue> = {
  label?: SemanticShorthandItem<HtmlLabelProps>;
  value?: TValue;
  renderValue?: (value: TValue | undefined) => React.ReactNode;
  textarea?: boolean;
  'data-testid'?: string;
};

export const ReadonlyFormValue = <TValue,>({
  label,
  value,
  textarea,
  renderValue,
  'data-testid': dataTestId,
}: ReadonlyFormValueProps<TValue>) => {
  const renderLabel = () => {
    if (!label) return null;

    // If label is a string or ReactNode
    if (typeof label === 'string' || typeof label === 'number' || React.isValidElement(label)) {
      return <label>{label}</label>;
    }

    // If label is an object with props
    if (typeof label === 'object' && !React.isValidElement(label) && 'content' in label) {
      const { content, ...rest } = label as Partial<HtmlLabelProps> & { content?: React.ReactNode };
      return <label {...rest}>{content}</label>;
    }

    // Fallback for other ReactNode types
    return null;
  };

  return (
    <FormField>
      {renderLabel()}
      <S.ValueContainer textarea={textarea} data-testid={dataTestId}>
        {renderValue ? renderValue(value) : value != null ? String(value) : ''}
      </S.ValueContainer>
    </FormField>
  );
};

const S = {
  ValueContainer: styled.div<{ textarea?: boolean }>`
    && {
      margin-bottom: 1em;
      margin-top: 0.5em;
      ${(props) => props.textarea && 'min-height: 75px;'}
    }
  `,
};
